import { Link } from "@tanstack/react-router";
import hot from "../../../assets/img/hot.png"
import { cleanUrl, ipfs2md5 } from "../../../utils";
import { getAnyCollMeta } from "../../../lib/api";
import { useEffect, useState } from "react";
import { imgErr } from "../../imgErrHandler/imgErrHandler";

function CollectionListItem({
  path1 , path2 , path3 ,
  img1 ,
  text1 , text2 , full, rerender
}) {
  const chainLogo = full && (full.chainId != null || full.network != null)
    ? `/assets/img/chain-logos/chain-${full.chainId ?? full.network}.png`
    : undefined;
  // Compute the CDN src synchronously so SSR prepopulates <img src=...> with
  // the valid /c/NN/<md5>.jpg?<b64-original> URL (ipfs2md5 is a pure md5 of
  // the cleanUrl-normalized gateway link; btoa is global in Node 16+ too).
  const computeSrc = (initialUrl: string | undefined) => {
    if (initialUrl && typeof initialUrl === 'string') {
      const imgSrc = ipfs2md5(initialUrl, true) + ".jpg";
      try {
        return { imgSrc, ld: "?" + btoa(initialUrl) };
      } catch (e) {
        console.error("Could not btoa img1 for longdesc:", initialUrl, e);
        return { imgSrc, ld: '' };
      }
    }
    return { imgSrc: '', ld: '' };
  };
  const [imgSrc, setImgSrc] = useState(() => computeSrc(img1).imgSrc);
  const [ld, setLd] = useState(() => computeSrc(img1).ld);
  const [loadFailed, setLoadFailed] = useState(() => !imgSrc);
  const [loadDelayed, setLoadDelayed] = useState(false);

  const updateImageSource = async (initialUrl) => {
    setLoadFailed(false);
    setLoadDelayed(false);
    const { imgSrc: src, ld: query } = computeSrc(initialUrl);
    setImgSrc(src);
    setLd(query);
  };

  const handleImageError = async (e) => {
    setLoadFailed(true);
    // Hide the broken <img> element immediately so browser never draws its icon
    const target = e.currentTarget as HTMLImageElement;
    if (target) {
      target.style.display = 'none';
      target.style.opacity = '0';
    }
    try {
      const fallbackData = await getAnyCollMeta(full.id);
      const fallbackImg = fallbackData?.metadata?.image || fallbackData?.image;
      if (fallbackImg) {
        const fallbackMeta = { meta: { image: fallbackImg } };
        imgErr(e, fallbackMeta);
      } else {
        setImgSrc('');
      }
    } catch (err) {
      console.error("Failed to fetch fallback image metadata:", err);
      setImgSrc('');
    }
  };

  useEffect(()=>{
    updateImageSource(img1);
  }, [img1, rerender, full.id]);

  // Show animated gradient after a delay if no image has loaded
  useEffect(() => {
    if (!imgSrc) {
      const timer = setTimeout(() => setLoadDelayed(true), 900);
      return () => clearTimeout(timer);
    } else {
      setLoadDelayed(false);
    }
  }, [imgSrc]);

  path2="/";
  path3="/";

  return(
      <div className="col-xl-2 col-md-2 mb-xl-0 fix-cards">
        <div className="pixel-collection-card">
          <div className={`pixel-collection-image ${loadFailed || loadDelayed ? 'is-failed' : ''}`}>
            <Link className="pixel-collection-link" to={path1} state={{ imageUrl: img1, imgSrc, ld }}>
              {loadFailed ? (
                <div className="pixel-collection-placeholder" aria-hidden="true" />
              ) : loadDelayed ? (
                <div className="pixel-collection-placeholder is-delayed" aria-hidden="true" />
              ) : (
                <img
                  src={imgSrc ? imgSrc + ld : undefined}
                  alt=""
                  style={{ objectFit: "cover", width: "100%", aspectRatio: "4/4", overflow: "hidden" }}
                  longdesc={ld}
                  className="pixel-collection-img"
                  onError={(e) => handleImageError(e)}
                />
              )}
            </Link>
          </div>
          <Link to={path2} className="pixel-owner-icon hot-icon" data-bs-toggle="tooltip" data-bs-placement="bottom" title="" data-bs-original-title="view profile">
             <img alt="placeholder" src={chainLogo || hot} />
          </Link>
          <div className="pixel-collection-body">
            <p className="pixel-collection-name">{text1 ? text1 : <br/>}</p>
            <Link className="pixel-collection-link" to={path3}>
              <h5 className="pixel-collection-symbol">{text2 ? text2 : <br/>}</h5>
            </Link>
          </div>
        </div>
      </div>
  )
}

export default CollectionListItem
